/**
 * App.js — Tegan Web UI
 * =====================
 * Auth shell (App) wraps the inner TeganApp so useTegan only mounts
 * after authentication is confirmed — preventing the connect/disconnect
 * race caused by auth state changes remounting the hook.
 */

import { useState }           from 'react';
import { Menu }               from 'lucide-react';
import { useTegan }           from './hooks/useTegan';
import { useManifestGroups }  from './hooks/useManifestGroups';
import { useAuth }            from './hooks/useAuth';
import ConnectionBadge        from './components/ConnectionBadge';
import ToolGrid               from './components/ToolGrid';
import ChatPanel              from './components/ChatPanel';
import MetricsPanel           from './components/MetricsPanel';
import LoginScreen            from './components/LoginScreen';

function formatToolName(name) {
  return (name || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Inner app — only mounted after auth is confirmed. useTegan lives here. */
function TeganApp({ logout }) {
  const { manifest, connected, messages, sendCommand, connectionStatus, metrics, clearHistory } =
    useTegan();

  const { groups, toolCount } = useManifestGroups(manifest);

  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [activeToolForm, setActiveToolForm] = useState(null);

  const handleToolClick = (tool) => {
    if (tool.inputs?.length > 0) {
      setActiveToolForm(tool);
    } else {
      const cmd = tool.display_name || formatToolName(tool.name);
      sendCommand(cmd);
    }
    setSidebarOpen(false);
  };

  return (
    <div className="flex flex-col overflow-hidden bg-[#1a1a1a] text-white font-sans" style={{height: '100dvh'}}>

      <header className="shrink-0 flex items-center justify-between px-4 py-2.5 bg-[#111111] border-b border-[#2a2a2a]">
        <div className="flex items-center gap-2.5">
          <button
            className="md:hidden text-[#aaa] hover:text-[#CC5500] active:text-[#CC5500] transition-colors focus:outline-none mr-1"
            onClick={() => setSidebarOpen(v => !v)}
            aria-label={sidebarOpen ? 'Close tools' : 'Open tools'}
          >
            <Menu size={20} />
          </button>

          <div className="w-8 h-8 rounded-full border-2 border-[#CC5500] bg-[#2a2a2a] flex items-center justify-center font-bold text-[#CC5500] shrink-0 text-sm">
            T
          </div>

          <h1 className="text-base font-bold tracking-[0.25em] text-white select-none">
            TEGAN
          </h1>

          {toolCount > 0 && (
            <span className="text-[#555] text-xs select-none">
              {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
            </span>
          )}

          <button
            onClick={logout}
            className="text-[#444] hover:text-[#CC5500] text-xs transition-colors ml-2"
            title="Sign out"
          >
            Sign out
          </button>

          <button
            onClick={clearHistory}
            className="text-[#444] hover:text-[#CC5500] text-xs transition-colors ml-2"
            title="Clear chat history"
          >
            Clear history
          </button>
        </div>

        <ConnectionBadge connectionStatus={connectionStatus} />
      </header>

      <div className="flex-1 flex overflow-hidden min-h-0">

        <div className="hidden md:flex w-64 shrink-0 h-full flex-col">
          <ToolGrid groups={groups} onToolClick={handleToolClick} />
        </div>

        <div className={`
          md:hidden fixed inset-y-0 left-0 z-40 w-64
          bg-[#161616] border-r border-[#2a2a2a]
          transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <ToolGrid groups={groups} onToolClick={handleToolClick} />
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/60 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="flex-1 h-full overflow-hidden">
          <ChatPanel
            messages={messages}
            sendCommand={sendCommand}
            connected={connected}
            activeToolForm={activeToolForm}
            setActiveToolForm={setActiveToolForm}
          />
        </div>

        <MetricsPanel metrics={metrics} />
      </div>
    </div>
  );
}

/** Outer shell — handles auth only, never touches useTegan. */
export default function App() {
  const { user, authError, login, logout, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-[#555] text-sm">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onLogin={login} error={authError} />;
  }

  return <TeganApp logout={logout} />;
}